import { useState } from "react";
import {
  saveValidValue,
  updateValidValue,
  deleteValidValue,
} from "../services/template-service";
import { useNotification } from "../../../context/notification-provider";
import type { ValidValueItem } from "../types/api-types";

interface UseValidValuesManagementProps {
  templateId: number;
  mutate: () => void;
}

export const useValidValuesManagement = ({
  templateId,
  mutate,
}: UseValidValuesManagementProps) => {
  const { showNotification } = useNotification();

  // Add/Edit state
  const [openDialog, setOpenDialog] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editId, setEditId] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [valueToDelete, setValueToDelete] = useState<{
    id: number;
    value: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setNewValue("");
    setEditIndex(null);
    setEditId(0);
  };

  const handleOpenEditDialog = (index: number, item: ValidValueItem) => {
    setOpenDialog(true);
    setNewValue(item.value);
    setEditIndex(index);
    setEditId(item.id);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewValue("");
    setEditIndex(null);
    setEditId(0);
  };

  const handleSave = async () => {
    if (!newValue.trim()) return;

    setIsSaving(true);
    try {
      if (editIndex === null) {
        // Add new value
        await saveValidValue({
          id: 0,
          templateId,
          value: newValue.trim(),
        });
        showNotification({
          message: "Valid value added successfully",
          severity: "success",
        });
      } else {
        // Update existing value
        await updateValidValue({
          id: editId,
          templateId,
          value: newValue.trim(),
        });
        showNotification({
          message: "Valid value updated successfully",
          severity: "success",
        });
      }
      mutate();
      handleCloseDialog();
    } catch (err) {
      showNotification({
        message: `Failed to save valid value. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (id: number, value: string) => {
    setValueToDelete({ id, value });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!valueToDelete) return;

    setIsDeleting(true);
    try {
      await deleteValidValue(valueToDelete.id);
      mutate();
      showNotification({
        message: "Valid value deleted successfully",
        severity: "success",
      });
      setDeleteConfirmOpen(false);
      setValueToDelete(null);
    } catch (err) {
      showNotification({
        message: `Failed to delete valid value. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setValueToDelete(null);
  };

  return {
    // Add/Edit dialog state
    openDialog,
    newValue,
    editIndex,
    isSaving,
    setNewValue,
    handleOpenDialog,
    handleOpenEditDialog,
    handleCloseDialog,
    handleSave,

    // Delete dialog state
    deleteConfirmOpen,
    valueToDelete,
    isDeleting,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};

export default useValidValuesManagement;
